import type { WritableStateSource } from '@ngrx/signals'
import { patchState, signalStore, withState } from '@ngrx/signals'
import type { Signal } from '@angular/core'
import { inject, Injector, signal } from '@angular/core'
import { isFunction } from '@awork/core/state/signal-store/helpers'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit'
import { withStateStorage } from '@awork/core/state/signal-store/withStorage'
import { withNoop } from '@awork/core/state/signal-store/withNoop'
import { SignalStoreHistory } from '@awork/core/state/signal-store/signalStoreHistory'
import { PersistState } from '@awork/core/state/signal-store/persistState'
import { isPersistStatePlainDeleteStorage } from '@awork/core/state/signal-store/helpers'

export type StoreState<State extends object> = WritableStateSource<State> & { [K in keyof State]: Signal<State[K]> }

export interface StoreOptions<State> {
  name: string
  initialState: State
  resettable?: boolean
  persist?: boolean
  persistDelay?: number
  excludePersistProps?: (keyof State)[]
}

/**
 * Signal store for state.
 * Provides functions to update the state.
 *
 * ### Usage example
 * ```ts
 * @Injectable({ providedIn: 'root' })
 * export class AppStore extends SignalStore<AppState> {
 *   constructor() {
 *     super({
 *       resettable: true,
 *       name: 'user',
 *       initialState: createInitialState(),
 *     })
 *   }
 * }
 * ```
 */
export class SignalStore<State extends object> {
  private store: StoreState<State>

  private storeOptions: StoreOptions<State>

  protected readonly isLoading = signal(true)
  private readonly error = signal(null)
  private readonly loadedOn = signal<Date>(null)

  readonly history: SignalStoreHistory<State>

  private signalStoreService = inject(SignalStoreService<State>)
  private injector = inject(Injector)
  private shouldRestorePersistedState = true
  private hasCompletedRestoreTracking = true
  private isRestoreHydrating = false
  private pendingRestoreOperations: (() => void)[] = []

  private readonly DEFAULT_STORE_OPTIONS: Partial<StoreOptions<State>> = {
    resettable: true,
    persist: true
  }

  private integrateDevtools: boolean

  constructor(storeOptions?: StoreOptions<State>) {
    this.applyStoreOptions(storeOptions)

    this.createStore()

    this.signalStoreService.registerStore(this.storeOptions.name, this)
    this.history = new SignalStoreHistory(this)
  }

  /**
   * Applies the store options, merging them with the default store options
   * @param {StoreOptions<State>} storeOptions
   */
  private applyStoreOptions(storeOptions: StoreOptions<State>): void {
    this.storeOptions = {
      ...this.DEFAULT_STORE_OPTIONS,
      ...storeOptions
    }
  }

  /**
   * Creates the signal store
   */
  private createStore(): void {
    this.integrateDevtools = localStorage.getItem('awReduxDevtools') === 'true'

    const StoreFactory = signalStore(
      { protectedState: false },
      this.integrateDevtools ? withDevtools(this.storeOptions.name) : withNoop(),
      withState(this.storeOptions.initialState),
      this.storeOptions.persist
        ? withStateStorage(
            this,
            {
              key: this.storeOptions.name,
              persistDelay: this.storeOptions.persistDelay,
              excludeProps: this.storeOptions.excludePersistProps
            },
            this.injector
          )
        : withNoop()
    )

    this.store = new StoreFactory() as StoreState<State>
  }

  /**
   * Gets the store
   * @returns {StoreState<State>}
   */
  getState(): StoreState<State> {
    return this.store
  }

  /**
   * Gets the store options
   * @returns {StoreOptions<State>}
   */
  getStoreOptions(): StoreOptions<State> {
    return this.storeOptions
  }

  /**
   * Marks this store as fully restored after hydration completed.
   */
  markAsRestored(): void {
    this.signalStoreService.markStoreAsRestored(this.storeOptions.name)
  }

  /**
   * Determines whether persisted state should still be applied.
   * @returns {boolean}
   */
  canApplyPersistedRestore(): boolean {
    return this.shouldRestorePersistedState
  }

  /**
   * Starts tracking local write operations until persistence restore completes.
   */
  beginRestoreTracking(): void {
    this.shouldRestorePersistedState = true
    this.hasCompletedRestoreTracking = false
    this.isRestoreHydrating = false
    this.pendingRestoreOperations = []
  }

  /**
   * Marks that persisted state is currently being applied to the store.
   */
  startRestoreHydration(): void {
    this.isRestoreHydrating = true
  }

  /**
   * Finishes restore tracking and replays queued local write operations on top of restored state.
   * @param {boolean} hydrated
   */
  finishRestoreTracking(hydrated: boolean): void {
    this.isRestoreHydrating = false
    this.hasCompletedRestoreTracking = true

    if (!hydrated) {
      this.pendingRestoreOperations = []

      return
    }

    const pendingRestoreOperations = [...this.pendingRestoreOperations]

    this.pendingRestoreOperations = []
    pendingRestoreOperations.forEach(replayOperation => replayOperation())
  }

  /**
   * Hook that is called before updating a state in the store
   * @param {State} prevState
   * @param {Partial<State>} nextState
   * @returns {Partial<State>}
   * @internal
   */
  preUpdateState(prevState: State, nextState: Partial<State>): Partial<State> {
    return nextState
  }

  /**
   * Applies the preUpdateState hook to the state before updating it in the store
   * @param {State} prevState
   * @param {Partial<State> | ((state: State) => Partial<State>)} newState
   * @returns {Partial<State>}
   */
  private applyPreUpdateState(
    prevState: State,
    newState: Partial<State> | ((state: State) => Partial<State>)
  ): Partial<State> {
    const nextState = isFunction(newState) ? newState(prevState) : newState
    const mergedNextState = { ...prevState, ...nextState }

    return this.preUpdateState(prevState, mergedNextState)
  }

  /**
   * Sets the loading state
   * @param {boolean} isLoading
   * @param {Date | null} loadedOn
   */
  setLoading(isLoading: boolean, loadedOn?: Date | null): void {
    this.queueRestoreOperation(() => this.setLoading(isLoading, loadedOn))
    this.applyLoadingState(isLoading, loadedOn)
  }

  /**
   * Applies the loading state without affecting restore replay tracking.
   * @param {boolean} isLoading
   * @param {Date | null} loadedOn
   */
  private applyLoadingState(isLoading: boolean, loadedOn?: Date | null): void {
    const wasLoading = this.isLoading()
    const normalizedLoadedOn = loadedOn ? new Date(loadedOn) : null

    if (wasLoading === isLoading) {
      if (!isLoading && normalizedLoadedOn) {
        this.loadedOn.set(normalizedLoadedOn)
      }

      return
    }

    this.isLoading.set(isLoading)

    if (isLoading) {
      this.loadedOn.set(null)

      return
    }

    this.loadedOn.set(normalizedLoadedOn || new Date())
  }

  /**
   * Gets the loading state
   * @returns {Signal<boolean>}
   */
  getIsLoading(): Signal<boolean> {
    return this.isLoading
  }

  /**
   * Gets the loaded on date
   * @returns {Signal<Date | null>}
   */
  getLoadedOn(): Signal<Date | null> {
    return this.loadedOn
  }

  /**
   * Sets the error state
   * @param {Error} error
   */
  setError(error: Error): void {
    this.queueRestoreOperation(() => this.setError(error))
    this.applyErrorState(error)
  }

  /**
   * Applies the error state without affecting restore replay tracking.
   * @param {Error} error
   */
  private applyErrorState(error: Error): void {
    this.error.set(error)
  }

  /**
   * Gets the error state
   * @returns {Signal<Error>}
   */
  getError(): Signal<Error> {
    return this.error
  }

  /**
   * Updates the state in the store
   * @param {Partial<State>} newState
   * @param {string} action
   * @example
   * update({ value: 'newValue' })
   * update(state => ({ value: state.value + 'newValue' }))
   */
  update(newState: Partial<State>, action?: string): void
  update(newStateFn: (state: State) => Partial<State>, action?: string): void
  update(
    newStateOrFn: Partial<State> | ((state: State) => Partial<State>),
    action = this.storeOptions.name + ': update'
  ): void {
    this.queueRestoreUpdateOperation(newStateOrFn, action)

    this.integrateDevtools
      ? updateState(this.store, action, state => this.applyPreUpdateState(state, newStateOrFn))
      : patchState(this.store, state => this.applyPreUpdateState(state, newStateOrFn))

    if (this.isLoading()) {
      this.applyLoadingState(false)
    }
  }

  /**
   * Resets the store by setting the state to the initial state
   * @param {string} [action=this.storeOptions.name + ': reset']
   */
  reset(action = this.storeOptions.name + ': reset'): void {
    this.shouldRestorePersistedState = false
    this.hasCompletedRestoreTracking = true
    this.isRestoreHydrating = false
    this.pendingRestoreOperations = []
    this.clearPersistedStore()

    this.integrateDevtools
      ? updateState(this.store, action, this.storeOptions.initialState)
      : patchState(this.store, this.storeOptions.initialState)

    this.setLoading(true)
    this.applyErrorState(null)
  }

  /**
   * Clears the persisted store entry immediately from IndexedDB.
   * This bypasses debounced persistence and ensures reset state is applied right away.
   */
  private clearPersistedStore(): void {
    const storage = PersistState.instance?.storage

    if (!this.storeOptions.persist || !isPersistStatePlainDeleteStorage(storage)) {
      return
    }

    storage.deleteItem(this.storeOptions.name).subscribe({
      error: () => {}
    })
  }

  /**
   * Queues a local write operation so it can be replayed after restore.
   * @param {() => void} replayOperation
   */
  private queueRestoreOperation(replayOperation: () => void): void {
    if (this.hasCompletedRestoreTracking || this.isRestoreHydrating) {
      return
    }

    this.pendingRestoreOperations.push(replayOperation)
  }

  /**
   * Queues a local state update so it can be replayed after restore.
   * @param {Partial<State> | ((state: State) => Partial<State>)} newStateOrFn
   * @param {string} action
   */
  private queueRestoreUpdateOperation(
    newStateOrFn: Partial<State> | ((state: State) => Partial<State>),
    action: string
  ): void {
    if (isFunction(newStateOrFn)) {
      this.queueRestoreOperation(() => this.update(newStateOrFn, action))

      return
    }

    this.queueRestoreOperation(() => this.update(newStateOrFn, action))
  }
}
